/**
 * Value types for the Web Share Target handoff.
 *
 * Web equivalent of `kaether_for_ios/Stoat/E2EE/ShareExtensionPayload.swift`.
 *
 * THREAT MODEL
 *   The Web Share Target is the browser's equivalent of iOS's Share
 *   Extension. A PWA declares `share_target` in its manifest and the OS /
 *   browser routes shared content (text, URL, files) to the PWA via an
 *   HTTP-like request the Service Worker intercepts. Two things make this
 *   a system boundary that deserves the same rigour as iOS's extension:
 *
 *   1. The shared payload is host-app-controlled. A malicious page can
 *      call `navigator.share({...})` with attacker-controlled bytes; a
 *      malicious app on Android can launch the PWA via the Android share
 *      sheet with whatever the OS allows. We treat every byte and every
 *      string as untrusted input.
 *   2. The Service Worker handler runs in a constrained execution context.
 *      It MUST NOT hit the network on startup, MUST NOT auto-send, MUST
 *      NOT bypass the in-app composer's validation. The shared payload
 *      lands in a draft-pending IndexedDB record; the user explicitly
 *      confirms in the composer before anything ships.
 *
 *   Wire shape on disk (IndexedDB):
 *
 *     kaether:share-target:v1 → `SharePendingDraft` JSON keyed by ULID
 *
 *   IndexedDB has no `.completeFileProtection` analogue on web; this is a
 *   documented platform gap (see `Signal_standard_web.md`).
 *
 *   The value types defend against:
 *
 *   - A hostile page handing the PWA a remote URL the SW would fetch.
 *     `ShareItem` has no `remoteURL` / `fetchURL` field by construction
 *     (and the SW handler never makes outbound fetches in response to a
 *     share). The plaintext bytes are extracted directly from the multipart
 *     `FormData` body the browser delivers.
 *   - A hostile page inflating an attachment past in-app size caps.
 *     `ShareTargetPolicy.validate` re-applies the per-category byte cap
 *     from `AttachmentPolicy` on top of a 50 MB total cap.
 *   - A hostile page declaring a permitted MIME but supplying bytes of an
 *     unsupported format. The SW handler treats MIME as a fast allow-list
 *     gate; the main-thread composer re-runs `AttachmentPolicy.decide`
 *     (which includes the magic-byte sniff) before encrypt.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. Pure types.
 */

/**
 * Closed enum of the kinds of items the OS share sheet can hand a PWA.
 * Mirrors iOS `ShareItemKind`.
 */
export type ShareItemKind = "text" | "url" | "image" | "video" | "file";

/**
 * One item handed to the SW by the browser via the share-target POST body.
 *
 * Invariant (enforced at validation time):
 *   - For `text` and `url`, exactly `plaintextString` is set and
 *     `plaintextBytes` is undefined.
 *   - For `image`, `video`, `file`, exactly `plaintextBytes` is set and
 *     `plaintextString` is undefined.
 *
 * The type INTENTIONALLY has no `remoteURL` / `fetchURL` / `assetIdentifier`
 * — only data already materialised in the request body. The SW has no
 * business calling the network on startup.
 */
export interface ShareItem {
  readonly kind: ShareItemKind;
  readonly mimeType?: string;
  readonly plaintextBytes?: Uint8Array;
  readonly plaintextString?: string;
  readonly suggestedFilename?: string;
}

/**
 * One queued share — the SW validated the payload, the user must still pick
 * a destination thread in the composer. The draft is persisted to IDB so a
 * navigation does not lose the bytes.
 *
 * `id` is a ULID so dashboards / debug logs can sort pending drafts in
 * `createdAt` order without parsing the JSON. The ULID is generated inside
 * the SW handler and never reused.
 *
 * `expiresAt` bounds the IDB retention so an abandoned share does not leave
 * plaintext bytes lying around indefinitely. Default expiry: 1 hour from
 * `createdAt` (enforced by `ShareTargetStore`).
 */
export interface SharePendingDraft {
  readonly id: string;
  /** Optional target channel — typically empty when the SW persists; the
   *  composer fills this in when the user picks. Kept here for parity with
   *  iOS's `SharePendingDraft.targetChannelId`. */
  readonly targetChannelId?: string;
  readonly items: readonly ShareItem[];
  readonly captionText?: string;
  /** Unix ms. */
  readonly createdAt: number;
  /** Unix ms. The composer rejects drafts past this point. */
  readonly expiresAt: number;
}

/**
 * Closed-set error type emitted by `ShareTargetPolicy.validate`. Each case
 * carries enough detail for a stable user-facing message + a debugging
 * counter. Mirrors iOS `SharePayloadError`.
 */
export type SharePayloadError =
  | { readonly kind: "noItemsProvided" }
  | { readonly kind: "tooManyItems"; readonly count: number }
  | { readonly kind: "unsupportedKind"; readonly detail: string }
  | { readonly kind: "captionTooLong"; readonly scalarCount: number }
  | { readonly kind: "invalidMimeType"; readonly mime: string }
  | { readonly kind: "oversizeAttachment"; readonly byteSize: number }
  | { readonly kind: "totalPayloadOverLimit"; readonly totalBytes: number };

/** Codec discriminator string used by the IDB record + redirect URL query. */
export const SHARE_TARGET_CODEC_TAG = "kaether:share-target:v1:" as const;

/**
 * Counts the number of unicode scalars in `s` (i.e. `[...s].length`), NOT
 * the number of UTF-16 code units (`s.length`). Required so emoji that
 * occupy two UTF-16 code units (or grapheme-cluster emoji like the rainbow
 * flag) count as the correct number of scalars.
 *
 * Mirrors Swift's `caption.unicodeScalars.count`.
 */
export function unicodeScalarCount(s: string): number {
  // `[...s]` iterates code points (the JavaScript "string iterator" yields
  // code points, NOT UTF-16 code units), which matches Swift's
  // UnicodeScalar semantics. `s.length` would over-count surrogate-pair
  // emoji like U+1F308 RAINBOW (2 UTF-16 code units, 1 scalar).
  let n = 0;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const _ of s) n += 1;
  return n;
}
